/*File to view database and add entries of conversations for dev testing.*/

import React, { useEffect, useState } from 'react';
import './DevelopmentAdmin.css';

const DevelopmentAdmin = () => {
  const [clients, setClients] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [formData, setFormData] = useState({
    username: '',
    brand: '',
    poc: '',
    status: 'new',
    terms: '',
    messageContent: '',
    sender: 'system',
    receiver: '',
    thread_id: '',
    context_summary: ''
  });
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedConversation, setSelectedConversation] = useState(null);

  // Fetch the list of clients
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/users`)
      .then((response) => response.json())
      .then((data) => setClients(data.results))
      .catch((error) => console.error('Error fetching clients:', error));
  }, []);

  // Fetch conversations for the selected client
  const fetchConversations = (username) => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/conversations`)
      .then((response) => response.json())
      .then((data) => setConversations(data.results))
      .catch((error) => console.error('Error fetching conversations:', error));
  };

  // Get CSRF token from the cookie
  const getCSRFToken = () => {
    const csrfToken = document.cookie
      .split(';')
      .find((cookie) => cookie.trim().startsWith('csrftoken='));
    return csrfToken ? csrfToken.split('=')[1] : null;
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Handle account deletion with confirmation
  const handleDelete = (clientId) => {
    const confirmed = window.confirm('Are you sure you want to delete this account?');
    if (confirmed) {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/user/${clientId}`, {
        method: 'DELETE',
        headers: {
          'X-CSRFToken': getCSRFToken(),
          // 'ngrok-skip-browser-warning': 'true',
        },
      })
        .then(() => {
          setClients(clients.filter((client) => client.id !== clientId));
        })
        .catch((error) => console.error('Error deleting client:', error));
    }
  };

  // Handle client selection
  const handleClientSelect = (client) => {
    setSelectedClient(client);
    fetchConversations(client.username); // Fetch conversations for this client
  };

  // Handle form submission for adding a new conversation and creating a deal
  const handleConversationSubmit = (e) => {
    e.preventDefault();
    const client = clients.find((client) => client.username === formData.username);

    if (client) {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/conversation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCSRFToken(),
          // 'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify({
          username: formData.username,  // Client username
          brand: formData.brand,  // Brand for the deal
          poc: formData.poc,  // Point of contact for the deal
          status: formData.status,  // Status of the deal
          terms: formData.terms,  // Terms of the deal
          thread_id: formData.thread_id,  // Thread ID for the conversation (optional)
          context_summary: formData.context_summary  // Context summary for the conversation (optional)
        }),
      })
        .then((response) => response.json())
        .then((data) => {
          alert('Conversation created successfully!');
          setFormData({
            username: '',
            brand: '',
            poc: '',
            status: 'new',
            terms: '',
            messageContent: '',
            thread_id: '',
            context_summary: ''
          });
          fetchConversations(client.username); // Refresh conversations for the client
        })
        .catch((error) => console.error('Error creating conversation:', error));
    } else {
      alert('Client not found');
    }
  };

  // Handle adding a new message to the selected conversation
  const handleAddMessage = (e) => {
    e.preventDefault();
    if (selectedConversation) {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/conversation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCSRFToken(),
          // 'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify({
          conversation_id: selectedConversation.id,
          content: formData.messageContent,
          sender: formData.sender,
          receiver: formData.receiver,
        }),
      })
        .then((response) => response.json())
        .then((data) => {
          alert('Message added successfully!');
          setFormData({ ...formData, messageContent: '' });  // Clear message input
        })
        .catch((error) => console.error('Error adding message:', error));
    } else {
      alert('Please select a conversation first.');
    }
  };

  return (
    <div className="admin-container">
      <div className="left-panel">
        <h2>Client Accounts</h2>
        <table>
          <thead>
            <tr>
              <th>Username</th>
              <th>Email</th>
              <th>Delete</th>
            </tr>
          </thead>
          <tbody>
            {clients.map((client) => (
              <tr key={client.id}>
                <td onClick={() => handleClientSelect(client)}>{client.username}</td>
                <td>{client.email}</td>
                <td>
                  <input type="checkbox" onClick={() => handleDelete(client.id)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="right-panel">
        <h2>Create Conversation</h2>
        <form onSubmit={handleConversationSubmit}>
          <label>Username:</label>
          <input type="text" name="username" value={formData.username} onChange={handleInputChange} required />

          <label>Brand:</label>
          <input type="text" name="brand" value={formData.brand} onChange={handleInputChange} required />

          <label>POC (Point of Contact):</label>
          <input type="text" name="poc" value={formData.poc} onChange={handleInputChange} />

          <label>Status:</label>
          <select name="status" value={formData.status} onChange={handleInputChange}>
            <option value="new">New</option>
            <option value="negotiating">Negotiating</option>
            <option value="info_requested">Info Requested</option>
            <option value="client_managed">Client Managed</option>
            <option value="accepted">Accepted</option>
            <option value="rejected">Rejected</option>
          </select>

          <label>Terms:</label>
          <textarea name="terms" value={formData.terms} onChange={handleInputChange} />

          <label>Thread ID (Optional):</label>
          <input type="text" name="thread_id" value={formData.thread_id} onChange={handleInputChange} />

          <label>Context Summary (Optional):</label>
          <textarea name="context_summary" value={formData.context_summary} onChange={handleInputChange} />

          <button type="submit">Create Conversation</button>
        </form>

        {selectedClient && (
          <>
            <h2>Select Conversation</h2>
            <select
              onChange={(e) => setSelectedConversation(conversations.find((conv) => conv.id === parseInt(e.target.value)))}
            >
              <option value="">Select a conversation</option>
              {conversations.map((conv) => (
                <option key={conv.id} value={conv.id}>
                  {conv.deal.brand} with {conv.deal.poc}
                </option>
              ))}
            </select>

            {selectedConversation && (
              <>
                <h2>Add Message to Conversation</h2>
                <form onSubmit={handleAddMessage}>
                  <label>Message Content:</label>
                  <textarea
                    name="messageContent"
                    value={formData.messageContent}
                    onChange={handleInputChange}
                    required
                  />

                  <label>Receiver:</label>
                  <input
                    type="text"
                    name="receiver"
                    value={formData.receiver}
                    onChange={handleInputChange}
                    placeholder="Receiver's email or phone number"
                    required
                  />

                  <button type="submit">Add Message</button>
                </form>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DevelopmentAdmin;


// import React, { useEffect, useState } from 'react';
// import './DevelopmentAdmin.css';

// const DevelopmentAdmin = () => {
//   const [clients, setClients] = useState([]);
//   const [conversations, setConversations] = useState([]);
//   const [formData, setFormData] = useState({
//     username: '',
//     brand: '',
//     poc: '',
//     status: 'new',
//     terms: '',
//     messageContent: '',
//     sender: 'system',
//     receiver: '',
//     thread_id: '',
//     context_summary: ''
//   });
//   const [selectedClient, setSelectedClient] = useState(null);
//   const [selectedConversation, setSelectedConversation] = useState(null);

//   // Fetch the list of clients
//   useEffect(() => {
//     fetch(`${process.env.REACT_APP_BACKEND_URL}/clients/`)
//       .then((response) => response.json())
//       .then((data) => setClients(data))
//       .catch((error) => console.error('Error fetching clients:', error));
//   }, []);

//   // Fetch conversations for the selected client
//   const fetchConversations = (username) => {
//     fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clients/${username}/conversations/`)
//       .then((response) => response.json())
//       .then((data) => setConversations(data))
//       .catch((error) => console.error('Error fetching conversations:', error));
//   };

//   // Get CSRF token from the cookie
//   const getCSRFToken = () => {
//     const csrfToken = document.cookie
//       .split(';')
//       .find((cookie) => cookie.trim().startsWith('csrftoken='));
//     return csrfToken ? csrfToken.split('=')[1] : null;
//   };

//   // Handle form input changes
//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setFormData({
//       ...formData,
//       [name]: value,
//     });
//   };

//   // Handle account deletion with confirmation
//   const handleDelete = (clientId) => {
//     const confirmed = window.confirm('Are you sure you want to delete this account?');
//     if (confirmed) {
//       fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clients/${clientId}/`, {
//         method: 'DELETE',
//         headers: {
//           'X-CSRFToken': getCSRFToken(),
//         },
//       })
//         .then(() => {
//           setClients(clients.filter((client) => client.id !== clientId));
//         })
//         .catch((error) => console.error('Error deleting client:', error));
//     }
//   };

//   // Handle client selection
//   const handleClientSelect = (client) => {
//     setSelectedClient(client);
//     fetchConversations(client.username); // Fetch conversations for this client
//   };

//   // Handle form submission for adding a new conversation and creating a deal
//   const handleConversationSubmit = (e) => {
//     e.preventDefault();
//     const client = clients.find((client) => client.username === formData.username);

//     if (client) {
//       fetch(`${process.env.REACT_APP_BACKEND_URL}/api/conversations/`, {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//           'X-CSRFToken': getCSRFToken(),
//         },
//         body: JSON.stringify({
//           username: formData.username,  // Client username
//           brand: formData.brand,  // Brand for the deal
//           poc: formData.poc,  // Point of contact for the deal
//           status: formData.status,  // Status of the deal
//           terms: formData.terms,  // Terms of the deal
//           thread_id: formData.thread_id,  // Thread ID for the conversation (optional)
//           context_summary: formData.context_summary  // Context summary for the conversation (optional)
//         }),
//       })
//         .then((response) => response.json())
//         .then((data) => {
//           alert('Conversation created successfully!');
//           setFormData({
//             username: '',
//             brand: '',
//             poc: '',
//             status: 'new',
//             terms: '',
//             messageContent: '',
//             thread_id: '',
//             context_summary: ''
//           });
//           fetchConversations(client.username); // Refresh conversations for the client
//         })
//         .catch((error) => console.error('Error creating conversation:', error));
//     } else {
//       alert('Client not found');
//     }
//   };

//   // Handle adding a new message to the selected conversation
//   const handleAddMessage = (e) => {
//     e.preventDefault();
//     if (selectedConversation) {
//       fetch(`${process.env.REACT_APP_BACKEND_URL}/api/conversations/add-message/`, {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//           'X-CSRFToken': getCSRFToken(),
//         },
//         body: JSON.stringify({
//           conversation: selectedConversation.id,
//           content: formData.messageContent,
//           sender: formData.sender,
//           receiver: formData.receiver,
//         }),
//       })
//         .then((response) => response.json())
//         .then((data) => {
//           alert('Message added successfully!');
//           setFormData({ ...formData, messageContent: '' });
//         })
//         .catch((error) => console.error('Error adding message:', error));
//     } else {
//       alert('Please select a conversation first.');
//     }
//   };

//   return (
//     <div className="admin-container">
//       <div className="left-panel">
//         <h2>Client Accounts</h2>
//         <table>
//           <thead>
//             <tr>
//               <th>Username</th>
//               <th>Email</th>
//               <th>Delete</th>
//             </tr>
//           </thead>
//           <tbody>
//             {clients.map((client) => (
//               <tr key={client.id}>
//                 <td onClick={() => handleClientSelect(client)}>{client.username}</td>
//                 <td>{client.email}</td>
//                 <td>
//                   <input type="checkbox" onClick={() => handleDelete(client.id)} />
//                 </td>
//               </tr>
//             ))}
//           </tbody>
//         </table>
//       </div>

//       <div className="right-panel">
//         <h2>Create Conversation</h2>
//         <form onSubmit={handleConversationSubmit}>
//           <label>Username:</label>
//           <input type="text" name="username" value={formData.username} onChange={handleInputChange} required />

//           <label>Brand:</label>
//           <input type="text" name="brand" value={formData.brand} onChange={handleInputChange} required />

//           <label>POC (Point of Contact):</label>
//           <input type="text" name="poc" value={formData.poc} onChange={handleInputChange} />

//           <label>Status:</label>
//           <select name="status" value={formData.status} onChange={handleInputChange}>
//             <option value="new">New</option>
//             <option value="negotiating">Negotiating</option>
//             <option value="info_requested">Info Requested</option>
//             <option value="client_managed">Client Managed</option>
//             <option value="accepted">Accepted</option>
//             <option value="rejected">Rejected</option>
//           </select>

//           <label>Terms:</label>
//           <textarea name="terms" value={formData.terms} onChange={handleInputChange} />

//           <label>Thread ID (Optional):</label>
//           <input type="text" name="thread_id" value={formData.thread_id} onChange={handleInputChange} />

//           <label>Context Summary (Optional):</label>
//           <textarea name="context_summary" value={formData.context_summary} onChange={handleInputChange} />

//           <button type="submit">Create Conversation</button>
//         </form>

//         {selectedClient && (
//           <>
//             <h2>Select Conversation</h2>
//             <select
//               onChange={(e) => setSelectedConversation(conversations.find((conv) => conv.id === parseInt(e.target.value)))}
//             >
//               <option value="">Select a conversation</option>
//               {conversations.map((conv) => (
//                 <option key={conv.id} value={conv.id}>
//                   {conv.deal.brand} with {conv.deal.poc}
//                 </option>
//               ))}
//             </select>

//             {selectedConversation && (
//               <>
//                 <h2>Add Message to Conversation</h2>
//                 <form onSubmit={handleAddMessage}>
//                   <label>Message Content:</label>
//                   <textarea
//                     name="messageContent"
//                     value={formData.messageContent}
//                     onChange={handleInputChange}
//                     required
//                   />

//                   <label>Receiver Email:</label>
//                   <input
//                     type="text"
//                     name="receiver"
//                     value={formData.receiver}
//                     onChange={handleInputChange}
//                     placeholder="Receiver's email"
//                     required
//                   />

//                   <button type="submit">Add Message</button>
//                 </form>
//               </>
//             )}
//           </>
//         )}
//       </div>
//     </div>
//   );
// };

// export default DevelopmentAdmin;

// import React, { useEffect, useState } from 'react';
// import './DevelopmentAdmin.css';

// const DevelopmentAdmin = () => {
//   const [clients, setClients] = useState([]);
//   const [conversations, setConversations] = useState([]);
//   const [formData, setFormData] = useState({
//     username: '',
//     brand: '',
//     poc: '',
//     status: 'new',
//     terms: '',
//     messageContent: '',
//     sender: 'system',
//     receiver: '',
//   });
//   const [selectedClient, setSelectedClient] = useState(null);
//   const [selectedConversation, setSelectedConversation] = useState(null);

//   // Fetch the list of clients
//   useEffect(() => {
//     fetch(`${process.env.REACT_APP_BACKEND_URL}/clients/`)
//       .then((response) => response.json())
//       .then((data) => setClients(data))
//       .catch((error) => console.error('Error fetching clients:', error));
//   }, []);

//   // Fetch conversations for the selected client
//   const fetchConversations = (username) => {
//     fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clients/${username}/conversations/`)
//       .then((response) => response.json())
//       .then((data) => setConversations(data))
//       .catch((error) => console.error('Error fetching conversations:', error));
//   };

//   // Get CSRF token from the cookie
//   const getCSRFToken = () => {
//     const csrfToken = document.cookie
//       .split(';')
//       .find((cookie) => cookie.trim().startsWith('csrftoken='));
//     return csrfToken ? csrfToken.split('=')[1] : null;
//   };

//   // Handle form input changes
//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setFormData({
//       ...formData,
//       [name]: value,
//     });
//   };

//   // Handle account deletion with confirmation
//   const handleDelete = (clientId) => {
//     const confirmed = window.confirm('Are you sure you want to delete this account?');
//     if (confirmed) {
//       fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clients/${clientId}/`, {
//         method: 'DELETE',
//         headers: {
//           'X-CSRFToken': getCSRFToken(), // Add the CSRF token for security
//         },
//       })
//         .then(() => {
//           setClients(clients.filter((client) => client.id !== clientId));
//         })
//         .catch((error) => console.error('Error deleting client:', error));
//     }
//   };

//   // Handle client selection
//   const handleClientSelect = (client) => {
//     setSelectedClient(client);
//     fetchConversations(client.username); // Fetch conversations for this client
//   };

//   // Handle form submission for adding a new conversation
//   const handleConversationSubmit = (e) => {
//     e.preventDefault();
//     const client = clients.find((client) => client.username === formData.username);

//     if (client) {
//       fetch(`${process.env.REACT_APP_BACKEND_URL}/api/conversations/`, {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//           'X-CSRFToken': getCSRFToken(),
//         },
//         body: JSON.stringify({
//           //user: client.id,
//           brand: formData.brand,
//           poc: formData.poc,
//           status: formData.status,
//           terms: formData.terms,
//         }),
//       })
//         .then((response) => response.json())
//         .then((data) => {
//           alert('Conversation created successfully!');
//           setFormData({
//             username: '',
//             brand: '',
//             poc: '',
//             status: 'new',
//             terms: '',
//             messageContent: '',
//           });
//           fetchConversations(client.username);
//         })
//         .catch((error) => console.error('Error creating conversation:', error));
//     } else {
//       alert('Client not found');
//     }
//   };

//   // Handle adding a new message to the selected conversation
//   const handleAddMessage = (e) => {
//     e.preventDefault();
//     if (selectedConversation) {
//       fetch(`${process.env.REACT_APP_BACKEND_URL}/api/conversations/add-message/`, {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//           'X-CSRFToken': getCSRFToken(),
//         },
//         body: JSON.stringify({
//           conversation: selectedConversation.id,
//           content: formData.messageContent,
//           sender: formData.sender,
//           receiver: formData.receiver,
//         }),
//       })
//         .then((response) => response.json())
//         .then((data) => {
//           alert('Message added successfully!');
//           setFormData({ ...formData, messageContent: '' });
//         })
//         .catch((error) => console.error('Error adding message:', error));
//     } else {
//       alert('Please select a conversation first.');
//     }
//   };

//   return (
//     <div className="admin-container">
//       <div className="left-panel">
//         <h2>Client Accounts</h2>
//         <table>
//           <thead>
//             <tr>
//               <th>Username</th>
//               <th>Email</th>
//               <th>Delete</th>
//             </tr>
//           </thead>
//           <tbody>
//             {clients.map((client) => (
//               <tr key={client.id}>
//                 <td onClick={() => handleClientSelect(client)}>{client.username}</td>
//                 <td>{client.email}</td>
//                 <td>
//                   <input type="checkbox" onClick={() => handleDelete(client.id)} />
//                 </td>
//               </tr>
//             ))}
//           </tbody>
//         </table>
//       </div>

//       <div className="right-panel">
//         <h2>Create Conversation</h2>
//         <form onSubmit={handleConversationSubmit}>
//           <label>Username:</label>
//           <input type="text" name="username" value={formData.username} onChange={handleInputChange} required />

//           <label>Brand:</label>
//           <input type="text" name="brand" value={formData.brand} onChange={handleInputChange} required />

//           <label>POC (Point of Contact):</label>
//           <input type="text" name="poc" value={formData.poc} onChange={handleInputChange} />

//           <label>Status:</label>
//           <select name="status" value={formData.status} onChange={handleInputChange}>
//             <option value="new">New</option>
//             <option value="negotiating">Negotiating</option>
//             <option value="info_requested">Info Requested</option>
//             <option value="client_managed">Client Managed</option>
//             <option value="accepted">Accepted</option>
//             <option value="rejected">Rejected</option>
//           </select>

//           <label>Terms:</label>
//           <textarea name="terms" value={formData.terms} onChange={handleInputChange} />

//           <button type="submit">Create Conversation</button>
//         </form>

//         {selectedClient && (
//           <>
//             <h2>Select Conversation</h2>
//             <select
//               onChange={(e) => setSelectedConversation(conversations.find((conv) => conv.id === parseInt(e.target.value)))}
//             >
//               <option value="">Select a conversation</option>
//               {conversations.map((conv) => (
//                 <option key={conv.id} value={conv.id}>
//                   {conv.deal.brand} with {conv.deal.poc}
//                 </option>
//               ))}
//             </select>

//             {selectedConversation && (
//               <>
//                 <h2>Add Message to Conversation</h2>
//                 <form onSubmit={handleAddMessage}>
//                   <label>Message Content:</label>
//                   <textarea
//                     name="messageContent"
//                     value={formData.messageContent}
//                     onChange={handleInputChange}
//                     required
//                   />

//                   <label>Receiver Email:</label>
//                   <input
//                     type="text"
//                     name="receiver"
//                     value={formData.receiver}
//                     onChange={handleInputChange}
//                     placeholder="Receiver's email"
//                     required
//                   />

//                   <button type="submit">Add Message</button>
//                 </form>
//               </>
//             )}
//           </>
//         )}
//       </div>
//     </div>
//   );
// };

// export default DevelopmentAdmin;
